import { useState, useEffect } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import "../styles/QrMenuPage.css";
import Nav from "./Nav/Nav";
import ScrollToTop from "../components/ScrollToTop";

/* ----- Օգնական ֆունկցիա slugify-ի համար (Unicode + Multi-lang) ----- */
function slugify(text) {
  const translitMap = {
    // Armenian
    Ա: "a", Բ: "b", Գ: "g", Դ: "d", Ե: "e", Զ: "z", Է: "e", Ը: "y", Թ: "t",
    Ժ: "zh", Ի: "i", Լ: "l", Խ: "kh", Ծ: "ts", Կ: "k", Հ: "h", Ձ: "dz", Ղ: "gh",
    Ճ: "ch", Մ: "m", Յ: "y", Ն: "n", Շ: "sh", Ո: "o", Չ: "ch", Պ: "p", Ջ: "j",
    Ռ: "r", Ս: "s", Վ: "v", Տ: "t", Ր: "r", Ց: "ts", Ւ: "v", Փ: "p", Ք: "q",
    Օ: "o", Ֆ: "f",
    ա: "a", բ: "b", գ: "g", դ: "d", ե: "e", զ: "z", է: "e", ը: "y", թ: "t",
    ժ: "zh", ի: "i", լ: "l", խ: "kh", ծ: "ts", կ: "k", հ: "h", ձ: "dz", ղ: "gh",
    ճ: "ch", մ: "m", յ: "y", ն: "n", շ: "sh", ո: "o", չ: "ch", պ: "p", ջ: "j",
    ռ: "r", ս: "s", վ: "v", տ: "t", ր: "r", ց: "ts", ւ: "v", փ: "p", ք: "q",
    օ: "o", ֆ: "f",
    // Russian
    А: "a", Б: "b", В: "v", Г: "g", Д: "d", Е: "e", Ё: "yo", Ж: "zh", З: "z",
    И: "i", Й: "y", К: "k", Л: "l", М: "m", Н: "n", О: "o", П: "p", Р: "r",
    С: "s", Т: "t", У: "u", Ф: "f", Х: "kh", Ц: "ts", Ч: "ch", Ш: "sh", Щ: "sch",
    Ъ: "", Ы: "y", Ь: "", Э: "e", Ю: "yu", Я: "ya",
    а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "yo", ж: "zh", з: "z",
    и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r",
    с: "s", т: "t", у: "u", ф: "f", х: "kh", ц: "ts", ч: "ch", ш: "sh", щ: "sch",
    ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
  };

  return text
    .toString()
    .split("")
    .map((ch) => translitMap[ch] || ch)
    .join("")
    .normalize("NFKD")
    .replace(/\s+/g, "-")
    .replace(/[^\p{L}\p{N}-]+/gu, "")
    .replace(/--+/g, "-")
    .toLowerCase()
    .trim();
}

/* ----- MenuSection Subcomponent ----- */
function MenuSection({ section, index, onItemClick, gridView }) {
  const sectionId = slugify(section.category);

  return (
    <div
      id={sectionId}
      className="qr-menu-section fade-in"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <h3 className="qr-menu-section-title">
        {section.category}
        {section.iconUrl && (
          <img
            src={section.iconUrl}
            alt="Category Icon"
            className="section-icon-iconUrl"
            style={{ marginLeft: "4px" }}
          />
        )}
      </h3>

      <ul className={`qr-menu-items ${gridView ? "grid-view" : "list-view"}`}>
        {section.items?.map((item, idx) => {
          const prevItem = idx > 0 ? section.items[idx - 1] : null;
          const hasImage = !!item.imageUrl;
          const prevHasImage = prevItem ? !!prevItem.imageUrl : hasImage;
          const isBreak = hasImage !== prevHasImage;

          return (
            <li
              key={idx}
              className={`qr-menu-item ${!hasImage ? "no-image" : ""} ${isBreak ? "row-break" : ""}`}
              onClick={() => onItemClick(item)}
            >
              {item.imageUrl && (
                <div className="qr-menu-item-image-wrapper">
                  <img
                    src={item.imageUrl}
                    alt={item.nameEn || item.nameHy || "Menu item image"}
                    className="qr-menu-item-image"
                    loading="lazy"
                  />
                  <div className="qr-menu-item-image-overlay" />
                </div>
              )}

              {(item.imageUrl || item.info || item.nameEn || item.nameHy) && (
                <div className="qr-menu-item-text">
                  <div className="qr-menu-item-line">
                    <span className="item-name-block">
                      {item.nameHy && <span className="item-name-hy">{item.nameHy}</span>}
                      {item.nameEn && <span className="item-name-en">{item.nameEn}</span>}
                    </span>
                    <span className="dots"></span>
                    <strong className="item-price">{item.price} AMD</strong>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ----- Main Component ----- */
export default function QrMenuPage() {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewItem, setPreviewItem] = useState(null);
  const [gridView, setGridView] = useState(true);

  const categories = [...new Set(menu.map((s) => s.category))];
  const categorySlugs = categories.map((cat) => ({
    name: cat,
    slug: slugify(cat),
  }));

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const q = query(collection(db, "menu"), orderBy("order"));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMenu(data);
      } catch (error) {
        console.error("Չհաջողվեց բեռնել մենյուն։", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, []);

  return (
    <div className="qr-menu-page">
      <Nav categories={categorySlugs} gridView={gridView} setGridView={setGridView} />




      {loading ? (
        <div className="loader" role="status" aria-live="polite">
          <span>Մենյուն բեռնվում է...</span>
        </div>
      ) : menu.length === 0 ? (
        <p className="empty-state">
          Մենյուն դեռ դատարկ է։ Խնդրում ենք փորձել ավելի ուշ կամ կապվել մեզ հետ։
        </p>
      ) : (
        menu.map((section, index) => (
          <div key={section.id}>
            <MenuSection
              section={section}
              index={index}
              onItemClick={setPreviewItem}
              gridView={gridView}
            />

            {index === 0 && (
              <div className="after-first-category-text"></div>
            )}
          </div>
        ))
      )}

      {/* Modal */}
      {previewItem && (
        <div className="image-modal" onClick={() => setPreviewItem(null)}>
          <div
            className="image-modal-content-wrapper"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="image-modal-close"
              onClick={() => setPreviewItem(null)}
            >
              ×
            </button>

            {previewItem.imageUrl ? (
              <img
                src={previewItem.imageUrl}
                alt={previewItem.nameEn || previewItem.nameHy}
                className="image-modal-content"
              />
            ) : (
              <div className="image-modal-no-image" />
            )}

            <div className="image-modal-info">
              {previewItem.nameHy && <p><strong>{previewItem.nameHy}</strong></p>}
              {previewItem.nameEn && <p><strong>{previewItem.nameEn}</strong></p>}
              {previewItem.info && <p>{previewItem.info}</p>}
              <p>Գին: {previewItem.price} AMD</p>
            </div>
          </div>
        </div>
      )}

      <ScrollToTop />
    </div>
  );
}
